// src/contexts/AppContext.tsx
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './AuthContext';
import type { Server, Friend, Channel, Profile, ServerInvite, UIMessage, Message, Reaction } from '../types';

type DiscoverServer = Server & {
  members_count: number;
  is_member: boolean;
  request_sent: boolean;
  owner_profile: Pick<Profile, 'username'> | null;
};

interface AppData {
    servers: Server[];
    friends: Friend[];
    pendingFriends: Friend[];
    serverInvites: ServerInvite[];
    channels: Channel[];
    allMembers: Map<string, Profile[]>;
    profiles: Map<string, Profile>;
    messages: Map<string, UIMessage[]>;
    discoverableServers: DiscoverServer[];
    adminData: { stats: any; allUsers: Profile[] } | null;
}

interface AppContextType {
    appData: AppData;
    isReady: boolean;
    refetchAll: () => Promise<void>;
    refetchServers: () => Promise<void>;
    refetchFriends: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const initialAppData: AppData = {
    servers: [],
    friends: [],
    pendingFriends: [],
    serverInvites: [],
    channels: [],
    allMembers: new Map(),
    profiles: new Map(),
    messages: new Map(),
    discoverableServers: [],
    adminData: null,
};

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [appData, setAppData] = useState<AppData>(initialAppData);
    const [isReady, setIsReady] = useState(false);

    const fetchAllData = useCallback(async () => {
        if (!user) {
            setIsReady(false);
            setAppData(initialAppData);
            return;
        }

        // Yüklemeye başlarken durumu sıfırla
        setIsReady(false);

        try {
            console.log('// Kapsamlı ön-yükleme başlatıldı...');
            const [
                membershipsRes,
                friendshipsRes,
                invitesRes,
            ] = await Promise.all([
                supabase.from('memberships').select('server_id').eq('user_id', user.id),
                supabase.from('friendships').select('*, user1:user1_id(*), user2:user2_id(*)').or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`),
                supabase.from('server_invites').select('*, servers(*), profiles:inviter_id(*)').eq('invitee_id', user.id).eq('status', 'pending'),
            ]);

            if (membershipsRes.error) throw friendshipsRes.error;
            if (friendshipsRes.error) throw friendshipsRes.error;
            if (invitesRes.error) throw invitesRes.error;
            
            const serverIds = membershipsRes.data?.map(m => m.server_id) || [];
            
            let servers: Server[] = [];
            let channels: Channel[] = [];
            let allMembers = new Map<string, Profile[]>();
            const profilesMap = new Map<string, Profile>();
            let messagesByChannelId = new Map<string, UIMessage[]>();
            let adminData: AppData['adminData'] = null;

            if (serverIds.length > 0) {
                 const [serversRes, channelsRes, allMembershipsRes] = await Promise.all([
                    supabase.from('servers').select('*').in('id', serverIds),
                    supabase.from('channels').select('*').in('server_id', serverIds),
                    supabase.from('memberships').select('server_id, user_id').in('server_id', serverIds)
                 ]);
                 if (serversRes.error) throw serversRes.error;
                 if (channelsRes.error) throw channelsRes.error;
                 if (allMembershipsRes.error) throw allMembershipsRes.error;

                 servers = serversRes.data || [];
                 channels = channelsRes.data || [];

                 const allMemberIds = [...new Set(allMembershipsRes.data.map(m => m.user_id))];
                 if (allMemberIds.length > 0) {
                     const { data: profilesData, error: profilesError } = await supabase.from('profiles').select('*').in('id', allMemberIds);
                     if (profilesError) throw profilesError;
                     (profilesData || []).forEach(p => profilesMap.set(p.id, p as Profile));
                     
                     const membersByServer = new Map<string, Profile[]>();
                     (allMembershipsRes.data || []).forEach(m => {
                         const profile = profilesMap.get(m.user_id);
                         if (profile) {
                             if (!membersByServer.has(m.server_id)) membersByServer.set(m.server_id, []);
                             membersByServer.get(m.server_id)!.push(profile);
                         }
                     });
                     allMembers = membersByServer;
                 }
                
                const textChannelIds = channels.filter(c => c.type === 'text').map(c => c.id);
                if (textChannelIds.length > 0) {
                    const messagePromises = textChannelIds.map(id => supabase.from('messages').select(`*`).eq('channel_id', id).order('created_at', { ascending: false }).limit(30));
                    const messageResults = await Promise.all(messagePromises);

                    const allMessages: Message[] = messageResults.flatMap(res => res.data || []);
                    const messageIds = allMessages.map(m => m.id);
                    
                    const messageUserIds = new Set(allMessages.map(m => m.user_id));
                    const newProfileIdsToFetch = [...messageUserIds].filter(id => !profilesMap.has(id));
                    
                    if (newProfileIdsToFetch.length > 0) {
                        const { data: newProfiles } = await supabase.from('profiles').select('*').in('id', newProfileIdsToFetch);
                        (newProfiles || []).forEach(p => profilesMap.set(p.id, p as Profile));
                    }

                    const { data: reactionsData } = await supabase.from('message_reactions').select(`*, profiles(id, username)`).in('message_id', messageIds);
                    const reactionsByMessageId = new Map<string, Reaction[]>();
                    (reactionsData || []).forEach(r => {
                        if (!reactionsByMessageId.has(r.message_id)) reactionsByMessageId.set(r.message_id, []);
                        reactionsByMessageId.get(r.message_id)!.push(r as Reaction);
                    });

                    allMessages.forEach(msg => {
                        if (!messagesByChannelId.has(msg.channel_id)) messagesByChannelId.set(msg.channel_id, []);
                        const completeMessage: UIMessage = {
                            ...msg,
                            profiles: profilesMap.get(msg.user_id) || null,
                            reactions: reactionsByMessageId.get(msg.id) || [],
                            status: 'sent'
                        };
                        messagesByChannelId.get(msg.channel_id)!.push(completeMessage);
                    });
                     messagesByChannelId.forEach((messages) => messages.reverse());
                }
            }
            
            if (user.email === 'kaankaramann55@gmail.com') {
                 const { data, error } = await supabase.functions.invoke('get-admin-stats');
                 if (error || data.error) console.error("Admin data fetch failed:", error || data.error);
                 else adminData = data;
            }

            const { data: discoverData } = await supabase.from('discoverable_servers').select('*');
            const userServerIds = new Set(serverIds);
            const { data: userInvites } = await supabase.from('server_invites').select('server_id').eq('invitee_id', user.id).eq('status', 'pending').eq('type', 'join_request');
            const pendingRequestServerIds = new Set((userInvites || []).map(i => i.server_id));
            const discoverableServers: DiscoverServer[] = (discoverData as any[] || []).map(s => ({ ...s, is_member: userServerIds.has(s.id), request_sent: pendingRequestServerIds.has(s.id) }));
            
            const acceptedFriends: Friend[] = [];
            const pendingFriends: Friend[] = [];
            (friendshipsRes.data || []).forEach(f => {
                const friendProfile = f.user1_id === user.id ? f.user2 : f.user1;
                if (!friendProfile) return;
                const friendObject: Friend = { ...(friendProfile as Profile), friendship_id: f.id, friendship_status: f.status, action_user_id: f.action_user_id };

                if (f.status === 'accepted') acceptedFriends.push(friendObject);
                else if (f.status === 'pending' && f.action_user_id !== user.id) pendingFriends.push(friendObject);
            });
            
            const serverInvites = (invitesRes.data || [])
                .filter(invite => invite.type !== 'join_request')
                .map(invite => {
                    const server = Array.isArray(invite.servers) ? invite.servers[0] : invite.servers;
                    const profile = Array.isArray(invite.profiles) ? invite.profiles[0] : invite.profiles;
                    return server && profile ? { ...invite, servers: server, profiles: profile } : null;
                })
                .filter((i): i is ServerInvite => i !== null);

            setAppData({ servers, friends: acceptedFriends, pendingFriends, serverInvites, channels, allMembers, profiles: profilesMap, messages: messagesByChannelId, discoverableServers, adminData });
            console.log('// Kapsamlı ön-yükleme tamamlandı. Uygulama hazır.');

        } catch (error) {
            console.error("Uygulama verileri başlatılamadı:", error);
            // Hata olsa bile, uygulamayı kilitli bırakmamak için initial (boş) veri ile devam et
            setAppData(initialAppData);
        } finally {
            // Hata olsa da olmasa da, yükleme ekranını kaldır
            setIsReady(true);
        }
    }, [user]);

    useEffect(() => {
        if(user) {
            fetchAllData();
        } else {
            // Kullanıcı çıkış yaptığında durumu sıfırla
            setIsReady(false);
            setAppData(initialAppData);
        }
    }, [user, fetchAllData]);

    const refetchServers = useCallback(async () => { /* ... */ }, [user]);
    const refetchFriends = useCallback(async () => { /* ... */ }, [user]);

    const value = useMemo(() => ({
        appData,
        isReady,
        refetchAll: fetchAllData,
        refetchServers,
        refetchFriends,
    }), [appData, isReady, fetchAllData, refetchServers, refetchFriends]);

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};
